import { useEffect, useRef, useState } from "react";
import { answerHint, checkAnswer } from "@buildlab/shared/exit-ticket"; // not the package root: that would ship the screening lists to browsers
import { api, type BragOffer, type ExitTicketScore, type SubmissionStatus, lessonLabel } from "./api";
import { BragToParents } from "./BragToParents";
import { Check } from "./icons";
import { emitStep } from "./mission-steps";
import "./grading.css";

// Submit with reflection (PRD FR-24): the top bar's Submit button opens the assignment's exit-ticket
// questions. Submitting saves a checkpoint, the answers and a timestamp; resubmitting keeps history.
// Exit tickets are scored out of 10 (decision 86): the same rule as the server (packages/shared
// exit-ticket.ts) gives live feedback under each answer, and the score shows after submitting.

const time = (t: number) => new Date(t).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });

function SendIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

export function SubmitButton({ projectId, lesson, disabled, beforeOpen }: { projectId: string; lesson: number; disabled: boolean; beforeOpen: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="pill-dark" data-step-panel="submit" title="Submit your work" disabled={disabled} onClick={() => setOpen(true)}>
        <SendIcon />
        <span className="bar-label">Submit</span>
      </button>
      {open && <SubmitDialog projectId={projectId} lesson={lesson} beforeOpen={beforeOpen} onClose={() => setOpen(false)} />}
    </>
  );
}

/** The score card after submitting: the score out of 10 and, kindly, what's still missing. */
function ScoreCard({ ticket, questions, tips }: { ticket: ExitTicketScore; questions: string[]; tips: Map<string, string> }) {
  const full = ticket.score === ticket.outOf;
  return (
    <div className={`ticket-score ${full ? "full" : ""}`} aria-label="Your exit ticket score">
      <span className="eyebrow">Exit ticket</span>
      <b className="ticket-num">
        {ticket.score}
        <span>/{ticket.outOf}</span>
      </b>
      {full ? (
        <p>Full credit. Every answer counts and every lesson check passed.</p>
      ) : (
        <div className="ticket-missing">
          <p>To get full credit, fix these and submit again:</p>
          <ul>
            {ticket.missing.answers.map((m) => (
              <li key={`a${m.index}`}>
                <b>Question {m.index + 1}</b>
                {questions[m.index] ? ` (${questions[m.index]})` : ""}: {TIP_FOR[m.problem]}
              </li>
            ))}
            {ticket.missing.checks.map((c) => (
              <li key={`c${c.id}`}>
                <b>Lesson check:</b> {tips.get(c.id) ?? c.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Shorter versions of the live tips, for the score card.
const TIP_FOR: Record<ExitTicketScore["missing"]["answers"][number]["problem"], string> = {
  empty: "write an answer.",
  short: "add a few more words (5 or more).",
  filler: "tell what you tried or what you think happened.",
  repeat: "say it without repeating the same words.",
  gibberish: "use real words to tell what happened.",
  copy: "answer in your own words, not the question's.",
};

function SubmitDialog({ projectId, lesson, beforeOpen, onClose }: { projectId: string; lesson: number; beforeOpen: () => Promise<void>; onClose: () => void }) {
  const [status, setStatus] = useState<SubmissionStatus | null>(null);
  const [answers, setAnswers] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ at: number; ticket: ExitTicketScore } | null>(null);
  // Brag to your parents (decision 83): the pop-up after a successful submit, when the server offers it.
  const [brag, setBrag] = useState<{ sid: number; offer: BragOffer } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLTextAreaElement>(null);

  const load = () =>
    api.submission(projectId).then(
      (s) => {
        setStatus(s);
        setAnswers((old) => (old.length === s.questions.length ? old : s.questions.map(() => "")));
      },
      (e: Error) => setError(e.message),
    );

  useEffect(() => {
    void beforeOpen().finally(() => void load()); // unsaved edits count toward the lesson checks
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [projectId]);

  useEffect(() => {
    if (status && !done) first.current?.focus();
  }, [status, done]);

  const questions = status?.questions ?? [];
  const checks = questions.map((q, i) => checkAnswer(answers[i] ?? "", q));
  const counting = checks.filter((c) => c.ok).length;
  const earlier = status?.submissions ?? [];
  const lessonChecks = status?.checks ?? [];
  const tips = new Map(lessonChecks.map((c) => [c.id, c.tip]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await beforeOpen(); // unsaved edits reach the server first, so the snapshot is what's on screen
      const r = await api.submit(projectId, answers);
      setDone({ at: r.submittedAt, ticket: r.exitTicket });
      emitStep({ kind: "submitted" }); // a mission step (D45)
      api.bragOffer(projectId, r.id).then(
        (offer) => {
          if (offer.offer && offer.preview) setBrag({ sid: r.id, offer });
        },
        () => {}, // no pop-up: the submit itself worked
      );
      void load(); // so "Fix my answers" shows the new count of submissions
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dlg-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      {brag && <BragToParents projectId={projectId} submissionId={brag.sid} offer={brag.offer} onClose={() => setBrag(null)} />}
      <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="submit-title">
        {done ? (
          <div className="dlg-done">
            <span className="done-mark" aria-hidden="true">
              <Check size={28} stroke="#fff" width={3} />
            </span>
            <h2 id="submit-title">Submitted</h2>
            <ScoreCard ticket={done.ticket} questions={questions} tips={tips} />
            <p>Your teacher can see your project as it is now, your answers, and how you worked with the helper.</p>
            <p className="muted small">{time(done.at)} · You can keep working and submit again; your teacher sees every version.</p>
            <div className="row">
              {done.ticket.score < done.ticket.outOf && (
                <button type="button" className="outline" onClick={() => setDone(null)}>
                  Fix my answers
                </button>
              )}
              <button type="button" className="primary" onClick={onClose}>
                Back to my project
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="dlg-head">
              <span className="eyebrow">Submit · {lessonLabel(lesson)}</span>
              <h2 id="submit-title">Exit ticket: look back at your work</h2>
              <p className="muted small">An answer counts when it has 5 or more words of your own. Your score is out of 10: your answers plus this lesson's checks.</p>
              {earlier.length > 0 && (
                <p className="muted small">
                  You submitted this {earlier.length === 1 ? "once" : `${earlier.length} times`} already (last: {time(earlier[0]!.submittedAt)}, exit ticket{" "}
                  {earlier[0]!.exitTicket.score}/{earlier[0]!.exitTicket.outOf}). Submitting again keeps the earlier ones.
                </p>
              )}
            </div>
            {!status && !error && <p className="muted">Loading the questions…</p>}
            {status && (
              <div className="dlg-body">
                {lessonChecks.length > 0 && (
                  <section className="lesson-checks" aria-label="Lesson checks">
                    <h3>
                      Lesson checks: {lessonChecks.filter((c) => c.pass).length} of {lessonChecks.length} done
                    </h3>
                    <ul>
                      {lessonChecks.map((c) => (
                        <li key={c.id} className={c.pass ? "ok" : "todo"}>
                          <span className="lc-mark" aria-hidden="true">
                            {c.pass ? <Check size={12} stroke="currentColor" width={3} /> : null}
                          </span>
                          <span>
                            <span className="sr-only">{c.pass ? "Done: " : "Not done yet: "}</span>
                            {c.tip}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {lessonChecks.some((c) => !c.pass) && <p className="muted small">Not done yet? Press Cancel, do it, then Submit again. Every check counts toward your score.</p>}
                  </section>
                )}
                {questions.map((q, i) => {
                  const c = checks[i]!;
                  const typed = (answers[i] ?? "").trim().length > 0;
                  return (
                    <label key={i} className="reflect">
                      <span>{q}</span>
                      <textarea
                        ref={i === 0 ? first : undefined}
                        id={`reflection-${i + 1}`}
                        rows={3}
                        maxLength={2000}
                        aria-describedby={`reflection-${i + 1}-hint`}
                        value={answers[i] ?? ""}
                        onChange={(e) => setAnswers((all) => all.map((a, j) => (j === i ? e.target.value : a)))}
                      />
                      <span id={`reflection-${i + 1}-hint`} className={`answer-hint ${c.ok ? "ok" : typed ? "todo" : ""}`} aria-live="polite">
                        {c.ok && <Check size={12} stroke="currentColor" width={3} />}
                        {answerHint(answers[i] ?? "", q)}
                      </span>
                    </label>
                  );
                })}
                <p className="answer-count small" aria-live="polite">
                  {counting === questions.length
                    ? `All ${questions.length} answers count.`
                    : questions.some((_, i) => !(answers[i] ?? "").trim())
                      ? `${counting} of ${questions.length} answers count so far. Answer every question to submit.`
                      : `${counting} of ${questions.length} answers count so far. You can submit, but answers that don't count yet won't earn points.`}
                </p>
              </div>
            )}
            {error && <p className="dlg-error">{error}</p>}
            <div className="dlg-foot">
              <button type="button" className="linklike" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="primary" disabled={!status || busy || questions.some((_, i) => !(answers[i] ?? "").trim())} onClick={() => void submit()}>
                {busy ? "Submitting…" : "Submit"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
